import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { getUserID } from '../useSessionUser'
import {
  DocumentData,
  DocumentResponse,
  IDocumentResponse,
  IIdentityDocument,
  IPrescription,
  PrescriptionResponse,
  StepResponseMap,
  StepSendDocumentsManager,
  StepsSendDocuments
} from './types'
import { useCameraCapture } from './useCameraCapture'
import { useDocumentStates } from './useDocumentStates'
import { useFileClearManager } from './useFileClearManager'

declare global {
  interface Window {
    Clicksign: any
  }
}

/**
 * Permite reaproveitar o fluxo fora das rotas /enviar-receita, /enviar-documentos etc.
 * Na área de associados o pedido não vem da URL e a navegação acontece por query string,
 * então quem consome passa o order_id e assume o controle das transições de etapa.
 */
export interface UseSendDocumentsOptions {
  orderId?: string | number | null
  /** Fluxos sem o toggle de leitura automática (associação) enviam sem validação por OCR */
  automaticReadingDefault?: boolean
  /** Na associação o laudo médico é sempre exigido, independente do tipo de produto */
  alwaysRequireLaudo?: boolean
  /** Associação exclui documento/receita pela rota dedicada (só `excluded`, sem MFA e sem passar por `denied`) */
  useAssociationDocumentRoutes?: boolean
  navigation?: {
    /** Etapa seguinte ao envio da receita */
    toOtherDocuments?: () => void
    toLaudoMedico?: () => void
    toAnvisa?: () => void
    /** Fim do fluxo — detalhe do pedido */
    toOrderDetails?: () => void
    /** Usado quando o pedido não é encontrado */
    toCart?: () => void
  }
}

export const useSendDocuments = (onSuccess?: () => void, options?: UseSendDocumentsOptions) => {
  const [currentStep, setCurrentStep] = useState<StepsSendDocuments>('prescription')
  const [showPowerOfAttorneySideSheet, setShowPowerOfAttorneySideSheet] = useState(false)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [widget, setWidget] = useState<any>(null)
  const [signatureStatus, setSignatureStatus] = useState<'pending' | 'completed'>('pending')
  const [existingDocuments, setExistingDocuments] = useState<any[]>([])
  const [existingPrescription, setExistingPrescription] = useState<any[]>([])
  const [existingPillPrescription, setExistingPillPrescription] = useState<any[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [orderData, setOrderData] = useState<any>(null)
  const [orderLoading, setOrderLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const automaticReadingDefault = options?.automaticReadingDefault ?? true
  const [automaticReadingEnabled, setAutomaticReadingEnabled] = useState(automaticReadingDefault)
  const [responseType, setResponseType] = useState<'normal' | 'ocr'>(automaticReadingDefault ? 'ocr' : 'normal')
  const [prescriptionFiles, setPrescriptionFiles] = useState<File[]>([])
  const [pillPrescriptionFiles, setPillPrescriptionFiles] = useState<File[]>([])
  const [addressFiles, setAddressFiles] = useState<File[]>([])
  const [identityFiles, setIdentityFiles] = useState<File[]>([])
  const [relationshipFiles, setRelationshipFiles] = useState<File[]>([])
  const [anvisaFiles, setAnvisaFiles] = useState<File[]>([])
  const [documentRefused, setDocumentRefused] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [missingBrandsList, setMissingBrandsList] = useState<any[]>([])
  const [prescriptionMatches, setPrescriptionMatches] = useState<Record<string, string[]>>({})
  const { order_id: order_id_from_route } = useParams() as { order_id: string }
  // O order_id explícito tem prioridade: fora das rotas de documento não existe param na URL
  const order_id = options?.orderId != null ? String(options.orderId) : order_id_from_route
  const user_id = getUserID()
  const history = useHistory()

  const navigation = options?.navigation
  const alwaysRequireLaudo = options?.alwaysRequireLaudo ?? false
  const goToOrderDetails = useCallback(() => {
    if (navigation?.toOrderDetails) {
      navigation.toOrderDetails()
      return
    }
    history.push(`/detalhes-do-pedido/${order_id}`)
  }, [navigation, history, order_id])

  const documentStates = useDocumentStates()
  const { capturePhoto } = useCameraCapture()

  const fileClearManager = useFileClearManager(
    {
      prescription: setPrescriptionFiles,
      pill_prescription: setPillPrescriptionFiles,
      address: setAddressFiles,
      identity: setIdentityFiles,
      affiliation: setRelationshipFiles,
      anvisa: setAnvisaFiles
    },
    order_id
  )

  const handleTrackOrder = () => {
    goToOrderDetails()
  }

  useEffect(() => {
    automaticReadingEnabled ? setResponseType('ocr') : setResponseType('normal')
  }, [automaticReadingEnabled])

  const manager = useMemo(() => {
    if (!order_id) return null
    return new StepSendDocumentsManager(order_id, 'COD B')
  }, [order_id])

  const extractFileDataFromUrl = (fileUrl: string) => {
    try {
      const decodedUrl = decodeURIComponent(fileUrl)

      const urlParts = decodedUrl.split('/')
      const protocolAndDomain = urlParts.slice(0, 3).join('/')
      const pathParts = urlParts.slice(3)

      if (pathParts.length === 0) {
        throw new Error('Caminho do arquivo não encontrado na URL')
      }

      const fileName = pathParts[pathParts.length - 1]

      const fullPath = pathParts.join('/')

      const folderParts = pathParts.slice(0, -1)
      const folderPath = folderParts.join('/')

      const folderURL = folderPath ? `${protocolAndDomain}/${folderPath}` : protocolAndDomain

      return {
        fileUrl,
        fileName,
        originalName: fileName,
        path: fullPath,
        folderURL
      }
    } catch (error) {
      console.error('Erro ao extrair dados da URL:', error)
      return null
    }
  }

  const requiresControlledPrescription = () =>
    (orderData?.order?.items || []).some((item: any) => item.requires_controlled_prescription)

  const canContinue = () => {
    const isPillOnly: boolean = orderData?.order?.summary?.is_pill_only ?? orderData?.order?.is_pill_only ?? false
    switch (currentStep) {
      case 'prescription':
        if (isPillOnly) {
          return !requiresControlledPrescription() || documentStates.pillPrescriptionSuccess
        }
        return (
          documentStates.receitaSuccess &&
          (!requiresControlledPrescription() || documentStates.pillPrescriptionSuccess)
        )
      case 'otherDocuments':
        return documentStates.addressSuccess && documentStates.identitySuccess
      case 'anvisa':
        return true
      case 'laudo_medico':
        return documentStates.laudoMedicoSuccess
      default:
        return false
    }
  }

  const sendDocuments = async (stepName: keyof StepResponseMap, responseType: 'normal' | 'ocr', requestFile: any) => {
    if (!manager || !requestFile) {
      const errorMessage = 'Dados inválidos para envio'
      documentStates.updateDocumentState(stepName, { error: errorMessage, isLoading: false, success: false })
      return { success: false, error: errorMessage }
    }

    const finalResponseType = stepName === 'laudo_medico' || stepName === 'pill_prescription' ? 'normal' : responseType

    await fileClearManager.clearRejectedDocuments()

    let response
    try {
      documentStates.updateDocumentState(stepName, { isLoading: true, error: null, success: false, progress: 0 })
      let endpoint = manager.getEndpoint(stepName, 'normal')
      const formData = new FormData()
      const description = manager.getDescription(stepName)

      formData.append('file', requestFile as any)
      formData.append('type', description)

      if (stepName === 'pill_prescription') {
        formData.append('is_for_pill', 'true')
      }

      if (stepName === 'prescription' || stepName === 'pill_prescription') {
        response = (await api(endpoint, {
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })) as PrescriptionResponse
      } else {
        response = (await api(endpoint, {
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })) as DocumentResponse
      }
      setDocumentId(response?.data?.recipeSalved?.id || response?.data?.document?.id || '')

      if (finalResponseType === 'ocr' && response?.data?.success) {
        const isPrescription = response?.data && 'recipeSalved' in response.data
        const fileUrl = isPrescription
          ? (response.data as any).recipeSalved.link_pdf
          : (response.data as any).document.url

        const extractedData = extractFileDataFromUrl(fileUrl)

        if (!extractedData) {
          throw new Error('Não foi possível extrair dados da URL do arquivo')
        }

        const body = {
          extension: isPrescription
            ? (response.data as any).recipeSalved.extension
            : (response.data as any).document.extension,
          type: description,
          documentId: isPrescription ? (response.data as any).recipeSalved.id : (response.data as any).document.id,
          patientId: user_id,
          fileUrl: extractedData.fileUrl,
          fileName: extractedData.fileName,
          originalName: extractedData.originalName,
          path: extractedData.path,
          folderURL: extractedData.folderURL,
          orderId: order_id,
          valid: true
        }

        endpoint = manager.getEndpoint(stepName, 'ocr')
        response = (await api(endpoint, {
          method: 'POST',
          data: body
        })) as IDocumentResponse<DocumentData>

        if (stepName === 'prescription') {
          const validations = (response as any).data?.valid?.report?.validations || []
          const brandValidation = validations.find((v: any) => v.rule === 'products_match_brand') 
          if (brandValidation?.details?.matches) {
            const matches = brandValidation.details.matches
            const newMatchedBrands = matches.map((m: any) => m.orderBrand)
            
            setPrescriptionMatches(prev => ({
              ...prev,
              [body.documentId]: newMatchedBrands
            }))
          }
        }

        if (!response.data.valid?.approved) {
          const identityDocumentData = stepName === 'identity' ? (response.data as IIdentityDocument) : null
          const identityResponsibleReason = identityDocumentData?.valid.requiresIdentityResponsibleDocument
            ? identityDocumentData.valid.identityResponsibleData?.reason
            : undefined

          const errorMessages = identityResponsibleReason
            ? [identityResponsibleReason]
            : response.data.valid?.reasons || [
                response.data.valid?.reason || 'Não foi possível validar o documento. Tente novamente ou envie outro arquivo.'
              ]
          setDocumentRefused(true)
          const errorMessage = Array.isArray(errorMessages) ? errorMessages[0] : errorMessages
          documentStates.updateDocumentState(stepName, { error: errorMessage, isLoading: false, success: false })
          return { success: false, error: errorMessages, data: response }
        }
      }

      await fetchExistingDocuments()
      documentStates.updateDocumentState(stepName, { success: true, isLoading: false, error: null, progress: 100 })
      fileClearManager.clearFiles(stepName, 1500)
      onSuccess?.()
      return { success: true, data: response }
    } catch (error: any) {
      console.error(error)
      const ocrCode = error?.response?.data?.code
      const ocrMessage = error?.response?.data?.message
      const errorMessage = ocrMessage || (error instanceof Error ? error.message : 'Não foi possível processar o documento agora. Tente novamente ou envie outro arquivo.')

      if (ocrCode) {
        const toastMessages: Record<string, string> = {
          'password-protected': 'O PDF está protegido por senha. Remova a senha e envie novamente.',
          'pdf-conversion-failed': 'Não foi possível ler o PDF. Verifique se o arquivo não está corrompido.',
          'no-text-found': 'Não conseguimos ler o documento. Envie uma foto mais nítida ou um PDF com texto legível.',
          'document-too-large': 'O documento é muito grande. Reduza o tamanho ou número de páginas.',
          'ai-processing-failed': 'Erro temporário ao processar o documento. Tente novamente em alguns instantes.',
          'download-failed': 'Não foi possível acessar o arquivo. Tente enviar novamente.',
        }
        toast.error(toastMessages[ocrCode] || errorMessage, { autoClose: 10000 })
      } else {
        toast.error(errorMessage, { autoClose: 8000 })
      }

      documentStates.updateDocumentState(stepName, { error: errorMessage, isLoading: false, success: false })
      return { success: false, error: errorMessage }
    } finally {
      documentStates.updateDocumentState(stepName, { isLoading: false })
    }
  }

  const updateDocument = useCallback(async (stepName: keyof StepResponseMap, documentId: string, orderId?: string) => {
    try {
      const response =
        stepName === 'prescription'
          ? await api(`/recipe/${documentId}/denied`, {
              method: 'PUT'
            })
          : await api(`/document/${documentId}/${orderId}/denied`, {
              method: 'PUT'
            })
      return { success: true, data: response }
    } catch (error) {
      console.error(error)
      toast.error('Erro ao atualizar documento')
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar documento' }
    }
  }, [])

  const deleteDocument = async (documentId: string) => {
    try {
      if (!options?.useAssociationDocumentRoutes) {
        await updateDocument(currentStep as keyof StepResponseMap, documentId, order_id)
      }

      const deleteUrl = options?.useAssociationDocumentRoutes
        ? `/document/association/${documentId}/${order_id}/excluded`
        : `/document/excluded/${documentId}`

      const response = await api(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.data?.success) {
        toast.success('Documento excluído com sucesso!')
        await fetchExistingDocuments()
      }

      return response
    } catch (error) {
      console.error(error)
      toast.error('Erro ao deletar documento')
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao deletar documento' }
    }
  }

  const deletePrescription = async (documentId: string) => {
    try {
      const deleteUrl = options?.useAssociationDocumentRoutes
        ? `/recipe/association/${documentId}/${order_id}/excluded`
        : `/recipe/excluded/${documentId}`

      const response = await api(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.data?.success) {
        toast.success('Receita excluída com sucesso!')
        setPrescriptionMatches(prev => {
          const copy = { ...prev }
          delete copy[documentId]
          return copy
        })
        await fetchExistingDocuments()
      }

      return response
    } catch (error) {
      console.error(error)
      toast.error('Erro ao deletar receita')
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao deletar receita' }
    }
  }

  const goToCart = useCallback(() => {
    if (navigation?.toCart) {
      navigation.toCart()
      return
    }
    history.push('/carrinho')
  }, [navigation, history])

  const fetchOrderData = useCallback(async () => {
    try {
      setOrderLoading(true)
      const { data } = await api.get(`/order/${order_id}`)
      if (data) {
        setOrderData(data)
      } else {
        toast.error('Pedido não encontrado')
        goToCart()
      }
    } catch (error) {
      console.error('Erro ao buscar dados do pedido:', error)
      toast.error('Erro ao carregar dados do pedido')
      goToCart()
    } finally {
      setOrderLoading(false)
    }
  }, [order_id, goToCart])

  const fetchExistingDocuments = useCallback(async () => {
    try {
      const response = await api.get(`/order/${order_id}/documents`)
      if (response.data?.success) {
        const documentsArrays = response.data.documents || []
        const flattenedDocuments = documentsArrays.flat()

        const docs = flattenedDocuments
        const recipies = response.data.recipies || []
        const normalRecipies = recipies.filter((recipe: any) => !recipe.is_for_pill)
        const pillRecipies = recipies.filter((recipe: any) => recipe.is_for_pill)

        setExistingDocuments(flattenedDocuments)
        setExistingPrescription(normalRecipies)
        setExistingPillPrescription(pillRecipies)

        documentStates.updateDocumentState('prescription', {
          success: normalRecipies.length > 0,
          isLoading: false,
          error: null
        })
        documentStates.updateDocumentState('pill_prescription', {
          success: pillRecipies.length > 0,
          isLoading: false,
          error: null
        })
        documentStates.updateDocumentState('address', {
          success: docs.some((doc: any) => doc.type === 'RESIDENCIAL'),
          isLoading: false,
          error: null
        })
        documentStates.updateDocumentState('identity', {
          success: docs.some((doc: any) => doc.type === 'IDENTIDADE'),
          isLoading: false,
          error: null
        })
        documentStates.updateDocumentState('affiliation', {
          success: docs.some((doc: any) => doc.type === 'VINCULO'),
          isLoading: false,
          error: null
        })
        documentStates.updateDocumentState('anvisa', {
          success: docs.some((doc: any) => doc.type === 'ANVISA'),
          isLoading: false,
          error: null
        })
        documentStates.updateDocumentState('laudo_medico', {
          success: docs.some((doc: any) => doc.type === 'LAUDO_MEDICO'),
          isLoading: false,
          error: null
        })
      }
    } catch (error) {
      console.error('Erro ao buscar documentos existentes:', error)
    }
  }, [order_id])

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
  }

  const handleFileCapture = (file: File, documentType?: string) => {
    switch (documentType) {
      case 'prescription':
        setPrescriptionFiles([file])
        break
      case 'pill_prescription':
        setPillPrescriptionFiles([file])
        break
      case 'address':
        setAddressFiles([file])
        break
      case 'identity':
        setIdentityFiles([file])
        break
      case 'relationship':
        setRelationshipFiles([file])
        break
      case 'anvisa':
        setAnvisaFiles([file])
        break
      default:
        setPrescriptionFiles([file])
    }
  }

  const handleTakePhoto = async () => {
    await capturePhoto({
      currentStep,
      onFileCapture: handleFileCapture,
      addressFiles,
      identityFiles,
      relationshipFiles
    })
  }

  const handlePrescriptionDrop = (files: File[]) => {
    setPrescriptionFiles(files)
  }

  const handlePrescriptionFileSelect = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files)
      setPrescriptionFiles(fileArray)
    }
  }

  const handlePillPrescriptionDrop = (files: File[]) => {
    setPillPrescriptionFiles(files)
  }

  const handlePillPrescriptionFileSelect = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files)
      setPillPrescriptionFiles(fileArray)
    }
  }

  const handleTakePhotoPill = async () => {
    await capturePhoto({
      currentStep: 'pill_prescription',
      onFileCapture: handleFileCapture,
      addressFiles,
      identityFiles,
      relationshipFiles
    })
  }

  const handleAddressDrop = (files: File[]) => {
    setAddressFiles(files)
  }

  const handleAddressFileSelect = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files)
      setAddressFiles(fileArray)
    }
  }

  const handleIdentityDrop = (files: File[]) => {
    setIdentityFiles(files)
  }

  const handleIdentityFileSelect = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files)
      setIdentityFiles(fileArray)
    }
  }

  const handleRelationshipDrop = (files: File[]) => {
    setRelationshipFiles(files)
  }

  const handleRelationshipFileSelect = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files)
      setRelationshipFiles(fileArray)
    }
  }

  const handleAnvisaDrop = (files: File[]) => {
    setAnvisaFiles(files)
  }

  const handleAnvisaFileSelect = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files)
      setAnvisaFiles(fileArray)
    }
  }

  const handleGenerateDocument = async () => {
    try {
      documentStates.updateDocumentState('anvisa', { isLoading: true, error: null })
      toast.info('Gerando procuração...')

      const body = {
        order_id
      }

      const { data } = await api.post(`/anvisaAuth/clickAutomation`, body)
      if (data.success) {
        toast.success('Procuração gerada com sucesso!')
        if (data?.data?.signerId) {
          setSignatureStatus('pending')
          setTimeout(() => {
            initializeWidget(data.data.signerId)
          }, 200)
        }

        documentStates.updateDocumentState('anvisa', { isLoading: false, success: true })
        return Promise.resolve()
      }
      toast.error('Erro ao gerar procuração')
      documentStates.updateDocumentState('anvisa', { isLoading: false, error: 'Erro ao gerar procuração' })
      return Promise.reject(new Error('Erro ao gerar procuração'))
    } catch (error) {
      console.error('Erro ao gerar procuração:', error)
      toast.error('Erro ao gerar procuração. Tente novamente.')
      documentStates.updateDocumentState('anvisa', { isLoading: false, error: 'Erro ao gerar procuração' })
      return Promise.reject(error)
    }
  }

  const handleSignDocument = async () => {
    try {
      setIsLoading(true)
      toast.info('Assinando documento...')

      const { data } = await api.post(`/order/${order_id}/sign-document`)

      if (data.success) {
        toast.success('Documento assinado com sucesso!')
        return Promise.resolve()
      }
      toast.error('Erro ao assinar documento')
      return Promise.reject(new Error('Erro ao assinar documento'))
    } catch (error) {
      console.error('Erro ao assinar documento:', error)
      toast.error('Erro ao assinar documento. Tente novamente.')
      return Promise.reject(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadDocument = async () => {
    try {
      setIsLoading(true)
      toast.info('Baixando documento...')

      const { data } = await api.get(`/order/${order_id}/download-signed-document`, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'procuração-assinada.pdf')
      document.body.appendChild(link)
      link.click()
      link.remove()

      toast.success('Documento baixado com sucesso!')
      return Promise.resolve()
    } catch (error) {
      console.error('Erro ao baixar documento:', error)
      toast.error('Erro ao baixar documento. Tente novamente.')
      return Promise.reject(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadStepByStep = () => {
    const pdfUrl =
      'https://cannect-upload-homolog.s3.us-east-2.amazonaws.com/Documentos/Modelos/PASSO-A-PASSO-ANVISA.pdf'
    window.open(pdfUrl, '_blank')
    toast.success('Abrindo passo a passo da ANVISA!')
  }

  const handleAnvisaSubmit = async () => {
    if (anvisaFiles.length === 0) {
      toast.info('Você pode continuar mesmo sem enviar a autorização agora. Um atendente irá te ajudar posteriormente.')
    }

    try {
      setIsLoading(true)

      if (anvisaFiles.length > 0) {
        const formData = new FormData()

        anvisaFiles.forEach((file) => {
          formData.append('anvisa_documents', file)
        })

        if (orderData?.id) {
          formData.append('order_id', orderData.id)
        }

        const { data } = await api.post(`/order_cart/upload-anvisa-documents`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (data.success) {
          toast.success('Autorização ANVISA enviada com sucesso!')
        }
      }
    } catch (error) {
      console.error('Erro ao processar autorização ANVISA:', error)
      toast.error('Erro ao processar autorização ANVISA. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const initializeWidget = useCallback((signerId: string) => {
    if (!signerId) return

    try {
      // @ts-expect-error - Clicksign is loaded globally
      // eslint-disable-next-line no-undef
      const generatedWidget = new Clicksign(signerId)

      generatedWidget.endpoint = import.meta.env.VITE_APP_CLICK_SIGN_URL
      generatedWidget.origin = window?.location?.origin
      generatedWidget.mount('clicksign-container')

      setWidget(generatedWidget)
      generatedWidget.on('signed', () => {
        setTimeout(() => {
          setSignatureStatus('completed')
          toast.success('Documento assinado com sucesso!')
        }, 1000)
      })

      generatedWidget.on('loaded', () => {
        console.log('Widget Clicksign carregado com sucesso')
      })
    } catch (error) {
      console.error('Erro ao inicializar widget Clicksign:', error)
      toast.error('Erro ao carregar widget de assinatura')
    }
  }, [])

  const clearWidget = useCallback(() => {
    if (widget) {
      try {
        widget.unmount()
      } catch (error) {
        console.error('Erro ao desmontar widget:', error)
      }
    }

    setWidget(null)
    setSignatureStatus('pending')

    const container = document.getElementById('clicksign-container')
    if (container) {
      container.innerHTML = ''
    }
  }, [widget])

  const resetSignatureStatus = useCallback(() => {
    setSignatureStatus('pending')
  }, [])

  const clearStates = () => {
    documentStates.resetAllStates()
    setDocumentId(null)
    setUploadedFiles([])
    fileClearManager.clearAllFiles()
  }

  const navigateToNextPage = () => {
    const currentStepNumber = getCurrentStepNumber()
    const nextStepNumber = currentStepNumber + 1

    const items = orderData?.order?.items?.filter((item: any) => item.product?.type === 'PRODUTO') || []

    const isPillOnly: boolean = orderData?.order?.summary?.is_pill_only ?? orderData?.order?.is_pill_only ?? false
    if (isPillOnly && currentStep === 'prescription') {
      goToOrderDetails()
      return
    }

    const hasLaudoProducts =
      alwaysRequireLaudo ||
      items.some((item: any) => item.product?.delivery_by_association === true || item.class_id === 4)
    const allProductsAreFreightBrand = items.every((item: any) => BRANDS_WITH_REQUIRED_FREIGHT.includes(item.brand_id))
    const allProductsDontNeedAnvisa = items.every(
      (item: any) =>
        item.product?.delivery_by_association === true ||
        item.class_id === 4 ||
        BRANDS_WITH_REQUIRED_FREIGHT.includes(item.brand_id)
    )

    const goToOtherDocuments = () =>
      navigation?.toOtherDocuments
        ? navigation.toOtherDocuments()
        : history.push(`/enviar-documentos/${order_id}`)

    const goToLaudoMedico = () =>
      navigation?.toLaudoMedico ? navigation.toLaudoMedico() : history.push(`/laudo-medico/${order_id}`)

    const goToAnvisa = () =>
      navigation?.toAnvisa ? navigation.toAnvisa() : history.push(`/autorizacao-anvisa/${order_id}`)

    switch (nextStepNumber) {
      case 2:
        goToOtherDocuments()
        break
      case 3:
        // O laudo precede a checagem de marca com frete: senão um pedido que exige
        // laudo pula direto para o detalhe do pedido sem nunca pedir o documento.
        if (hasLaudoProducts) {
          goToLaudoMedico()
        } else if (allProductsAreFreightBrand) {
          goToOrderDetails()
        } else {
          goToAnvisa()
        }
        break
      case 4:
        if (hasLaudoProducts && !allProductsDontNeedAnvisa && currentStep === 'laudo_medico') {
          goToAnvisa()
        } else {
          goToOrderDetails()
        }
        break
      case 5:
        goToOrderDetails()
        break
      default:
        console.warn('Página de destino não encontrada')
    }
  }

  const getCurrentStepNumber = () => {
    const items = orderData?.order?.items?.filter((item: any) => item.product?.type === 'PRODUTO') || []

    const hasLaudoProducts =
      alwaysRequireLaudo ||
      items.some((item: any) => item.product?.delivery_by_association === true || item.class_id === 4)
    const allProductsDontNeedAnvisa = items.every(
      (item: any) =>
        item.product?.delivery_by_association === true ||
        item.class_id === 4 ||
        BRANDS_WITH_REQUIRED_FREIGHT.includes(item.brand_id)
    )

    switch (currentStep) {
      case 'prescription':
        return 1
      case 'otherDocuments':
        return 2
      case 'laudo_medico':
        return 3
      case 'anvisa':
        if (hasLaudoProducts && !allProductsDontNeedAnvisa) {
          return 4
        }
        return 3
      default:
        return 1
    }
  }

  const handleContinue = async () => {
    const proceedToNextPage = async () => {
      if (!canContinue()) {
        toast.warning('Por favor, envie todos os documentos obrigatórios antes de continuar')
        return
      }
  
      try {
        documentStates.updateDocumentState(currentStep as keyof StepResponseMap, { isLoading: true })
        clearStates()
        navigateToNextPage()
      } catch (error) {
        console.error('Erro ao navegar:', error)
        toast.error('Erro ao navegar. Tente novamente.')
      } finally {
        documentStates.updateDocumentState(currentStep as keyof StepResponseMap, { isLoading: false })
      }
    }

    const isPillOnly: boolean = orderData?.order?.summary?.is_pill_only ?? orderData?.order?.is_pill_only ?? false

    if (currentStep === 'prescription' && automaticReadingEnabled && !isPillOnly) {
      const orderItems = orderData?.order?.items.filter((item: any) => item.product?.type === 'PRODUTO') || []
      const allMatchedBrands = Object.values(prescriptionMatches).flat()
      const missingBrands = orderItems
        .filter((item: any) => {
          return item.brand_name && !allMatchedBrands.includes(item.brand_name)
        })
        .map((item: any) => ({ name: item.brand_name, productName: item.name }))
      
      const uniqueMissingBrands = missingBrands.reduce((acc: any[], current: any) => {
        const existingBrand = acc.find(b => b.name === current.name);
        if (existingBrand) {
          if (!existingBrand.products.includes(current.productName)) {
            existingBrand.products.push(current.productName);
          }
        } else {
          acc.push({
            name: current.name,
            products: [current.productName]
          });
        }
        return acc;
      }, []);
      
      if (uniqueMissingBrands.length > 0) {
        setMissingBrandsList(uniqueMissingBrands)
        setIsConfirmModalOpen(true)
        return
      }
    }

    await proceedToNextPage()
  }

  const onKeepDocument = async () => {
    setDocumentRefused(false)
    await fetchExistingDocuments()
    documentStates.updateDocumentState(currentStep as keyof StepResponseMap, {
      success: true,
      isLoading: false,
      error: null,
      progress: 100
    })
    fileClearManager.clearFiles(currentStep as keyof StepResponseMap, 1500)
    onSuccess?.()
  }

  useEffect(() => {
    if (order_id) {
      fetchOrderData()
      fetchExistingDocuments()
    }
  }, [order_id, fetchOrderData, fetchExistingDocuments])

  useEffect(() => {
    if (orderLoading || !orderData) return

    const isPillOnly: boolean = orderData?.order?.summary?.is_pill_only ?? orderData?.order?.is_pill_only ?? false
    if (!isPillOnly) return

    const leaveToOrderDetails = () => {
      if (navigation?.toOrderDetails) {
        navigation.toOrderDetails()
        return
      }
      history.replace(`/detalhes-do-pedido/${order_id}`)
    }

    if (currentStep !== 'prescription') {
      leaveToOrderDetails()
      return
    }

    const hasControlledProduct = (orderData?.order?.items || []).some(
      (item: any) => item.product?.type === 'PRODUTO' && item.requires_controlled_prescription
    )

    if (!hasControlledProduct) {
      leaveToOrderDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderLoading, orderData, currentStep, history, order_id])

  return {
    currentStep,
    setCurrentStep,
    responseType,
    automaticReadingEnabled,
    showPowerOfAttorneySideSheet,
    setShowPowerOfAttorneySideSheet,
    sendDocuments,
    manager,
    deleteDocument,
    documentId,
    existingDocuments,
    existingPrescription,
    existingPillPrescription,
    prescriptionFiles,
    pillPrescriptionFiles,
    addressFiles,
    identityFiles,
    relationshipFiles,
    anvisaFiles,
    handleDragOver,
    handleDragLeave,
    handleTakePhoto,
    handlePrescriptionDrop,
    handlePrescriptionFileSelect,
    handlePillPrescriptionDrop,
    handlePillPrescriptionFileSelect,
    handleTakePhotoPill,
    handleAddressDrop,
    handleAddressFileSelect,
    handleIdentityDrop,
    handleIdentityFileSelect,
    handleRelationshipDrop,
    handleRelationshipFileSelect,
    handleAnvisaDrop,
    handleAnvisaFileSelect,
    uploadedFiles,
    setUploadedFiles,
    canContinue,
    handleContinue,
    fetchExistingDocuments,
    clearStates,
    orderData,
    orderLoading,
    fetchOrderData,
    deletePrescription,
    handleGenerateDocument,
    handleSignDocument,
    handleDownloadDocument,
    handleDownloadStepByStep,
    handleAnvisaSubmit,
    fileClearManager,
    widget,
    signatureStatus,
    clearWidget,
    resetSignatureStatus,
    handleTrackOrder,
    alwaysRequireLaudo,
    ...documentStates,
    prescriptionUploadState: documentStates.getDocumentState('prescription'),
    pillPrescriptionUploadState: documentStates.getDocumentState('pill_prescription'),
    addressUploadState: documentStates.getDocumentState('address'),
    identityUploadState: documentStates.getDocumentState('identity'),
    relationshipUploadState: documentStates.getDocumentState('affiliation'),
    anvisaUploadState: documentStates.getDocumentState('anvisa'),
    laudoMedicoUploadState: documentStates.getDocumentState('laudo_medico'),
    isLoading: documentStates.isGlobalLoading,
    error: documentStates.globalErrors.length > 0 ? documentStates.globalErrors[0] : null,
    success: documentStates.hasAnySuccess,
    documentRefused,
    setDocumentRefused,
    hasUploaded: false,
    setHasUploaded: () => {},
    setAutomaticReadingEnabled,
    onKeepDocument,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    missingBrandsList,
    proceedFromModal: async () => {
      setIsConfirmModalOpen(false)
      
      if (!canContinue()) {
        toast.warning('Por favor, envie todos os documentos obrigatórios antes de continuar')
        return
      }

      try {
        documentStates.updateDocumentState(currentStep as keyof StepResponseMap, { isLoading: true })
        clearStates()
        navigateToNextPage()
      } catch (error) {
        console.error('Erro ao navegar:', error)
        toast.error('Erro ao navegar. Tente novamente.')
      } finally {
        documentStates.updateDocumentState(currentStep as keyof StepResponseMap, { isLoading: false })
      }
    }
  }
}
